import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecentTestResults = ({ results, onViewAll, onViewResult }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'normal':
        return 'bg-success/10 text-success';
      case 'abnormal':
        return 'bg-warning/10 text-warning';
      case 'critical':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'normal':
        return 'CheckCircle2';
      case 'abnormal':
        return 'AlertTriangle';
      case 'critical':
        return 'AlertCircle';
      default:
        return 'FileText';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-secondary/10 rounded-lg">
            <Icon name="FlaskConical" size={20} color="var(--color-secondary)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">Recent Diagnostic Results</h2>
            <p className="text-xs md:text-sm text-muted-foreground">Latest diagnostic and imaging results</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName="ArrowRight"
          iconPosition="right"
          onClick={onViewAll}
        >
          <span className="hidden md:inline">View All</span>
          <span className="md:hidden">All</span>
        </Button>
      </div>
      <div className="space-y-3">
        {results?.length === 0 ? (
          <div className="text-center py-8 md:py-12">
            <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full mx-auto mb-4">
              <Icon name="FileX" size={32} className="text-muted-foreground" />
            </div>
            <p className="text-sm md:text-base text-muted-foreground">No diagnostic results available</p>
          </div>
        ) : (
          results?.map((result) => (
            <button
              key={result?.id}
              onClick={() => onViewResult(result)}
              className="w-full bg-background rounded-lg border border-border p-4 transition-smooth hover:shadow-elevation-2 text-left"
            >
              <div className="flex items-start gap-3">
                <div className={`flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${getStatusColor(result?.status)}`}>
                  <Icon name={getStatusIcon(result?.status)} size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="text-sm md:text-base font-semibold text-foreground">{result?.testName}</h3>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(result?.date)}</span>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground mb-2">{result?.orderedBy}</p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium px-2 py-1 rounded ${getStatusColor(result?.status)}`}>
                      {result?.statusLabel}
                    </span>
                    {result?.newResult && (
                      <span className="text-xs font-medium px-2 py-1 rounded bg-primary/10 text-primary">
                        New
                      </span>
                    )}
                  </div>
                </div>
                <Icon name="ChevronRight" size={18} className="text-muted-foreground flex-shrink-0" />
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentTestResults;